/**
 * WeeklyDigestPanel — the governed customer-facing weekly digest.
 *
 * THIN by design: every decision lives in pure exports —
 *   drafting .......... crm/digestComposer (real agent-worker via
 *                       argoWorker.runAgent when configured, deterministic
 *                       mergeFields fallback otherwise, honestly flagged)
 *   send .............. crm/digestSendPipeline.approveAndSend — the SINGLE
 *                       canonical send path (approval gate → src/lib/email.ts
 *                       outbox → Decision-Ledger seal). No other send exists.
 *   recall ............ crm/digestSendPipeline.recallDigest (compensating —
 *                       a sent email cannot be unsent, and the record says so)
 *   engagement ........ crm/digestEngagement (recorded outcomes, never
 *                       invented; the badge shows honest open/reply rates)
 */

import { useMemo, useState } from 'react';
import { CheckCircle2, Info, Mail, MailOpen, RotateCcw, Send, Sparkles } from 'lucide-react';
import { GlassPanel } from './GlassPanel';
import { C } from '../lib/tokens';
import { isConfigured as agentWorkerConfigured, runAgent } from '../lib/agent/argoWorker';
import { sendTransactionalEmail } from '../lib/email';
import { logActivity } from '../lib/db/activity';
import {
  buildGrounding,
  draftDigest,
  tailorForPersona,
  type DigestDraft,
  type DigestPersona,
} from '../lib/crm/digestComposer';
import {
  approveAndSend,
  recallDigest,
  toApprovableAction,
  type DigestRecallRecord,
  type PriorDigest,
  type SealedSendResult,
} from '../lib/crm/digestSendPipeline';
import {
  allDigestOutcomes,
  digestEfficacy,
  getDigestOutcome,
  recordEngagement,
  type DigestEngagementEvent,
} from '../lib/crm/digestEngagement';
import type { Account, Deliverable, ProjectTask, RoadmapItem, Ticket } from '../lib/db/types';

const PERSONA_LABEL: Record<DigestPersona, string> = { cfo: 'CFO', admin: 'Admin' };

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function WeeklyDigestPanel({
  accounts,
  tasks,
  tickets,
  deliverables,
  roadmap,
  canSend,
}: {
  accounts: Account[];
  tasks: ProjectTask[];
  tickets: Ticket[];
  deliverables: Deliverable[];
  roadmap: RoadmapItem[];
  canSend: boolean;
}) {
  const [accountId, setAccountId] = useState('');
  const [persona, setPersona] = useState<DigestPersona>('admin');
  const [draft, setDraft] = useState<DigestDraft | null>(null);
  const [drafting, setDrafting] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Session memory of the last sealed send per account — the diff baseline.
  const [priorByAccount, setPriorByAccount] = useState<Record<string, PriorDigest>>({});
  const [lastSend, setLastSend] = useState<SealedSendResult | null>(null);
  const [recall, setRecall] = useState<DigestRecallRecord | null>(null);
  // Bumped after every engagement write so the badge re-derives.
  const [engagementTick, setEngagementTick] = useState(0);

  const account = useMemo(
    () => accounts.find((a) => a.id === accountId) ?? null,
    [accounts, accountId],
  );

  // The ActionDiff-shaped proposal the Send button submits — recomputed
  // pure from the draft, so what you see IS what gets approved.
  const action = useMemo(() => {
    if (!draft || !account) return null;
    return toApprovableAction(draft, account, priorByAccount[account.id]);
  }, [draft, account, priorByAccount]);

  const efficacy = useMemo(() => {
    void engagementTick; // re-derive after every recorded event
    return digestEfficacy(allDigestOutcomes());
  }, [engagementTick]);

  const lastOutcome = useMemo(() => {
    void engagementTick;
    return lastSend ? getDigestOutcome(lastSend.sendId) : null;
  }, [lastSend, engagementTick]);

  async function onDraft() {
    if (!account || drafting) return;
    setDrafting(true);
    setError(null);
    setLastSend(null);
    setRecall(null);
    try {
      const grounding = buildGrounding(account, { tasks, tickets, deliverables, roadmap });
      // Real worker when configured; absent → the honest deterministic fallback.
      const next = await draftDigest(
        account,
        grounding,
        persona,
        agentWorkerConfigured() ? runAgent : undefined,
      );
      setDraft(next);
    } catch {
      setError('Drafting failed — try again.');
    } finally {
      setDrafting(false);
    }
  }

  function onPersona(next: DigestPersona) {
    setPersona(next);
    if (draft) setDraft(tailorForPersona(draft, next));
  }

  async function onSend() {
    if (!action || !account || sending) return;
    setSending(true);
    setError(null);
    try {
      // The ONE canonical send path: approval gate → outbox → ledger seal.
      const result = await approveAndSend(action, {
        approvalQueue: {
          approve: (a) => ({
            approved: window.confirm(
              `Approve sending "${a.subject}" to ${a.recipientName ?? a.to} <${a.to}>?\n\n` +
                `${a.reversibilityReason}`,
            ),
          }),
        },
        emailLog: {
          append: (entry) =>
            sendTransactionalEmail({
              to: entry.to,
              subject: entry.subject,
              textContent: entry.body,
              htmlContent: `<pre style="font-family:inherit;white-space:pre-wrap">${escapeHtml(entry.body)}</pre>`,
            }),
        },
        ledger: {
          seal: (entry) =>
            logActivity({
              event: 'executed',
              summary: `Weekly digest sent to ${action.recipientName ?? action.to} — sealed ${entry.hash}`,
              source: 'scheduler',
              kind: 'send_email',
              subjectId: entry.sendId,
            }).catch(() => undefined),
        },
      });
      recordEngagement(result.sendId, 'sent');
      setEngagementTick((t) => t + 1);
      setLastSend(result);
      setPriorByAccount((m) => ({
        ...m,
        [account.id]: { subject: action.subject, body: action.body },
      }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Send failed.');
    } finally {
      setSending(false);
    }
  }

  function onRecall() {
    if (!lastSend || recall) return;
    const record = recallDigest(lastSend.sendId, {
      reversal: {
        record: (rec) =>
          void logActivity({
            event: 'undone',
            summary: rec.note,
            source: 'scheduler',
            kind: rec.kind,
            subjectId: rec.reversesSendId,
          }).catch(() => undefined),
      },
    });
    setRecall(record);
  }

  function onEngagement(event: DigestEngagementEvent) {
    if (!lastSend) return;
    recordEngagement(lastSend.sendId, event);
    setEngagementTick((t) => t + 1);
  }

  return (
    <GlassPanel variant="default" style={{ padding: '18px 20px' }}>
      <div className="dash-section-head">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span
            aria-hidden="true"
            style={{
              width: 34,
              height: 34,
              borderRadius: 10,
              display: 'inline-grid',
              placeItems: 'center',
              background: 'linear-gradient(135deg, rgba(47,1,253,0.22), rgba(0,130,243,0.12))',
              border: '1px solid rgba(255,255,255,0.14)',
              color: C.blueElectric,
            }}
          >
            <Sparkles size={17} strokeWidth={2} />
          </span>
          <div>
            <span className="t-eyebrow">Governed customer email</span>
            <h2 className="t-section-title" style={{ margin: '4px 0 0' }}>
              Weekly Digest
            </h2>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span
            className="glass-pill"
            title="Book-wide engagement across every digest sent this session"
          >
            <MailOpen size={10} style={{ marginRight: 3 }} />
            {Math.round(efficacy.openRate * 100)}% open · {Math.round(efficacy.replyRate * 100)}%
            reply
          </span>
          {agentWorkerConfigured() ? (
            <span className="glass-pill glass-pill--blue" title="Prose drafted by the Argo agent-worker">
              Worker drafting
            </span>
          ) : (
            <span
              className="glass-pill"
              title="No agent-worker configured — drafts use the deterministic template and say so"
            >
              Deterministic fallback
            </span>
          )}
        </div>
      </div>

      <div className="t-meta" style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '0 0 10px' }}>
        <Info size={12} aria-hidden="true" />
        <span>
          Worker-drafted prose grounded ONLY in this account's live records, tailored per recipient
          persona. Every send needs your approval, is sealed in the ledger, and can be recalled
          with an honest compensating record.
        </span>
      </div>

      {/* Controls: account + persona + draft. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <select
          className="glass-input"
          value={accountId}
          onChange={(e) => {
            setAccountId(e.target.value);
            setDraft(null);
            setLastSend(null);
            setRecall(null);
            setError(null);
          }}
          style={{ flex: '0 1 220px', fontFamily: 'Manrope, sans-serif' }}
          title="Which account to draft this week's digest for"
        >
          <option value="">Choose an account…</option>
          {accounts.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </select>
        {(['cfo', 'admin'] as DigestPersona[]).map((p) => (
          <button
            key={p}
            className={`glass-btn${persona === p ? ' glass-btn--primary' : ''}`}
            onClick={() => onPersona(p)}
            title={
              p === 'cfo'
                ? 'Tailor emphasis for a finance/economic-buyer reader — value delivered first'
                : 'Tailor emphasis for a hands-on admin reader — operational changes and asks first'
            }
          >
            {PERSONA_LABEL[p]}
          </button>
        ))}
        <button
          className="glass-btn"
          onClick={() => void onDraft()}
          disabled={!account || drafting}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
          title="Draft this week's digest from the live records — nothing sends until you approve"
        >
          <Mail size={13} /> {drafting ? 'Drafting…' : 'Draft digest'}
        </button>
      </div>

      {error && (
        <p className="t-meta" style={{ margin: '10px 0 0', color: C.magenta }}>
          {error}
        </p>
      )}

      {!draft && !drafting && (
        <p className="t-meta" style={{ margin: '10px 0 0' }}>
          {account
            ? 'No draft yet — draft the digest to review its prose, diff, and provenance before sending.'
            : 'Choose an account to draft its weekly digest.'}
        </p>
      )}

      {draft && action && (
        <div style={{ display: 'grid', gap: 10, marginTop: 12 }}>
          {/* Draft: subject + body + provenance. */}
          <div className="glass-row" style={{ padding: '12px 14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span className="t-card-title" style={{ flex: 1, minWidth: 0 }}>
                {draft.subject}
              </span>
              <span
                className={`glass-pill${draft.provenance.deterministic ? '' : ' glass-pill--blue'}`}
                title={draft.provenance.note}
                style={{ fontSize: 10 }}
              >
                {draft.provenance.deterministic ? 'Deterministic template' : 'Worker-drafted'}
              </span>
              <span className="glass-pill" style={{ fontSize: 10 }} title="Recipient persona this draft is tailored for">
                {PERSONA_LABEL[draft.provenance.persona]} persona
              </span>
            </div>
            <pre
              className="t-meta"
              style={{ margin: '8px 0 0', whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}
            >
              {draft.body}
            </pre>
            <p className="t-meta" style={{ margin: '8px 0 0', fontSize: 11 }}>
              {action.to
                ? `Sends to ${action.recipientName} <${action.to}>`
                : 'No contact email on file — send disabled.'}
              {' · '}Grounded in {draft.provenance.groundedIn.length} record
              {draft.provenance.groundedIn.length === 1 ? '' : 's'}
            </p>
          </div>

          {/* Visual diff vs the prior send. */}
          <div className="glass-row" style={{ padding: '12px 14px' }}>
            <span className="t-card-title" style={{ fontSize: 13 }}>
              Diff vs prior send
            </span>
            <p className="t-meta" style={{ margin: '4px 0 0' }}>
              {action.diff.summary}
            </p>
            {action.diff.lines.length > 0 && (
              <pre className="t-meta" style={{ margin: '6px 0 0', whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
                {action.diff.lines.map((l) => (
                  <span
                    key={l}
                    style={{
                      display: 'block',
                      color: l.startsWith('+') ? C.green : l.startsWith('−') ? C.magenta : undefined,
                    }}
                  >
                    {l}
                  </span>
                ))}
              </pre>
            )}
          </div>

          {/* Governed send + recall + engagement. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            {canSend && !lastSend && (
              <button
                className="glass-btn glass-btn--primary"
                onClick={() => void onSend()}
                disabled={!action.to || sending}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
                title={
                  action.to
                    ? 'Opens the approval gate — nothing sends without your explicit approval, and the send is sealed in the ledger'
                    : 'No contact email on file — an address is never guessed'
                }
              >
                <Send size={13} /> {sending ? 'Sending…' : 'Send (approval required)'}
              </button>
            )}
            {lastSend && (
              <>
                <span className="glass-pill glass-pill--green" title={`Sealed ${lastSend.ledgerHash}`}>
                  <CheckCircle2 size={10} style={{ marginRight: 3 }} />
                  Sent · sealed
                </span>
                {recall ? (
                  <span className="glass-pill glass-pill--magenta" title={recall.note}>
                    Recalled — compensating record filed
                  </span>
                ) : (
                  <button
                    className="glass-btn"
                    onClick={onRecall}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
                    title="A sent email cannot be unsent — this files the honest compensating recall record"
                  >
                    <RotateCcw size={13} /> Recall
                  </button>
                )}
                {lastOutcome && (
                  <span
                    className="glass-pill"
                    style={{ fontSize: 10 }}
                    title="Engagement recorded against this send — observed events only, never inferred"
                  >
                    {lastOutcome.replied
                      ? 'Replied'
                      : lastOutcome.opened
                        ? 'Opened'
                        : 'Sent — no engagement yet'}
                    {lastOutcome.meetingBooked ? ' · meeting booked' : ''}
                  </span>
                )}
                {!lastOutcome?.opened && (
                  <button className="glass-btn" onClick={() => onEngagement('opened')} title="Record that the recipient opened this digest (observed, e.g. in your inbox tooling)">
                    Mark opened
                  </button>
                )}
                {!lastOutcome?.replied && (
                  <button className="glass-btn" onClick={() => onEngagement('replied')} title="Record that the recipient replied">
                    Mark replied
                  </button>
                )}
                {!lastOutcome?.meetingBooked && (
                  <button className="glass-btn" onClick={() => onEngagement('meeting_booked')} title="Record that this digest led to a booked meeting">
                    Meeting booked
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </GlassPanel>
  );
}
